import { FC } from "react";

import { type SectionAssessmentSummaryData } from "api/api-reporting-types";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, THead, TR, Table } from "components/tables/TableElements";
import { formatLocalePattern } from "i18n/helpers";
import { t } from "i18n/i18n";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { Link } from "utils/routing";
import { sectionHomeUrl } from "utils/urls";

type AssessmentSummaryTableProps = {
  data: SectionAssessmentSummaryData[];
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return "-";
  return formatLocalePattern(dateString, "common.formats.numeric");
};

const formatPercentage = (value: number) => {
  return `${value}%`;
};

export const AssessmentSummaryTable: FC<AssessmentSummaryTableProps> = ({ data }) => {
  return (
    <ScrollableTableContainer className="mb-0 max-h-[400px] overflow-y-auto">
      <Table>
        <THead>
          <tr>
            <TH>
              <TooltipTrigger
                content={t("assessment_summary_report.columns.course_tooltip")}
                place="top"
                positionStrategy="fixed"
              >
                {t("assessment_summary_report.columns.course")}
              </TooltipTrigger>
            </TH>

            <TH>{t("assessment_summary_report.columns.section_title")}</TH>

            <TH>
              <TooltipTrigger
                content={t("assessment_summary_report.columns.instructors_tooltip")}
                place="top"
                positionStrategy="fixed"
              >
                {t("assessment_summary_report.columns.instructors")}
              </TooltipTrigger>
            </TH>

            <TH>
              <TooltipTrigger
                content={t("assessment_summary_report.columns.last_assessed_tooltip")}
                place="top"
                positionStrategy="fixed"
              >
                {t("assessment_summary_report.columns.last_assessed")}
              </TooltipTrigger>
            </TH>

            <TH>
              <TooltipTrigger
                content={t("assessment_summary_report.columns.percent_assessed_tooltip")}
                place="top"
                positionStrategy="fixed"
              >
                {t("assessment_summary_report.columns.percent_assessed")}
              </TooltipTrigger>
            </TH>

            <TH>
              <TooltipTrigger
                content={t("assessment_summary_report.columns.percent_with_comments_tooltip")}
                place="top"
                positionStrategy="fixed"
              >
                {t("assessment_summary_report.columns.percent_with_comments")}
              </TooltipTrigger>
            </TH>

            <TH>
              <TooltipTrigger
                content={t("assessment_summary_report.columns.comment_length_tooltip")}
                place="top"
                positionStrategy="fixed"
              >
                {t("assessment_summary_report.columns.comment_length")}
              </TooltipTrigger>
            </TH>

            <TH>
              <TooltipTrigger
                content={t("assessment_summary_report.columns.wait_time_tooltip")}
                place="top"
                positionStrategy="fixed"
              >
                {t("assessment_summary_report.columns.wait_time")}
              </TooltipTrigger>
            </TH>

            <TH>
              <TooltipTrigger
                content={t("assessment_summary_report.columns.assessments_per_student_tooltip")}
                place="top"
                positionStrategy="fixed"
              >
                {t("assessment_summary_report.columns.assessments_per_student")}
              </TooltipTrigger>
            </TH>
          </tr>
        </THead>

        <tbody>
          {data.map((row) => (
            <TR key={row.id}>
              <TD>{row.course_code}</TD>

              <TD>
                <Link
                  className="block text-blue hover:text-blue-shade-40"
                  to={`${sectionHomeUrl(row.id)}?tab=assessments`}
                >
                  {row.title}
                </Link>
              </TD>

              <TD>{row.instructor_names.join(", ")}</TD>

              <TD className="text-center">{formatDate(row.date_last_assessed)}</TD>
              <TD className="text-right">{formatPercentage(row.assessment_percentage)}</TD>
              <TD className="text-right">
                {row.assessment_percentage > 0 ? formatPercentage(row.comment_percentage) : "-"}
              </TD>
              <TD className="text-right">
                {row.assessment_percentage > 0 ? row.avg_comment_length : "-"}
              </TD>
              <TD className="text-right">
                {row.assessment_percentage > 0 ? row.avg_assessment_delay_days : "-"}
              </TD>
              <TD className="text-right">
                {row.assessment_percentage > 0 ? row.avg_student_assessments : "-"}
              </TD>
            </TR>
          ))}
        </tbody>
      </Table>
    </ScrollableTableContainer>
  );
};
